import React, { useMemo, useState } from 'react';
import { quoteTotals } from '../../services/quoteDoc';
import { FOLLOW_UP_PRESETS, inDays, localDate, saveOutcome } from '../../services/dealOutcome';
import { retryChange, useSync } from '../../services/sync';
import './NextStep.css';

/**
 * Right after Send: the agent picks what happens next with this customer, then goes back to the Route page.
 *   Book Job     — which package they booked (Deal → Closed Won)
 *   Office Task  — what the office needs to do + due date (Zoho Task for the admins)
 *   Follow Up    — 3 days / 1 week / 1 month / a date + note (Zoho "Follow-up call" Task)
 * Picking one is required — every quote that goes out has a next step.
 *
 * The customer's report text (queued first on Send) shows here, and the agent can't save the next step
 * while it's still sending. Failed → the reason + Retry. No signal → it goes out on its own later.
 */

const OPTIONS = [
  { id: 'book', icon: '✓', title: 'Book Job', hint: 'They said yes — pick the package' },
  { id: 'office_task', icon: '📋', title: 'Office Task', hint: 'Something the office needs to do' },
  { id: 'follow_up', icon: '📅', title: 'Follow Up', hint: 'Call them back on a date' },
];

const money = (n) => `$${(Number(n) || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const niceDate = (iso) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '';

const fmtPhone = (e164) => {
  const d = String(e164 || '').replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : e164 || '';
};

// where the report text is: sending / sent / failed (with why) / waiting for signal / not texted
const useReportText = (reportText) => {
  const { changes, online } = useSync();
  if (!reportText || (!reportText.changeId && !reportText.why)) return null;
  if (!reportText.changeId) return { state: 'skipped', text: reportText.why };
  const c = changes.find((x) => x.id === reportText.changeId);
  const to = fmtPhone(reportText.to);
  if (!c || c.status === 'synced') return { state: 'sent', text: `✓ Report texted to ${to}` };
  if (!online) return { state: 'offline', text: `No signal — the report will be texted to ${to} when you’re back online` };
  if (c.status === 'failed') return { state: 'failed', text: `Report not texted: ${c.error || 'something went wrong'}`, changeId: c.id };
  return { state: 'sending', text: `Texting the report to ${to}…` };
};

// the report email: queued by the quotes job once the PDFs are uploaded, so it's found by inspection + time
const useReportEmail = (inspectionId, reportEmail) => {
  const { changes, online } = useSync();
  if (!reportEmail) return null;
  const c = [...changes].reverse().find((x) => x.type === 'report.email' && x.leadId === inspectionId && x.createdAt >= reportEmail.since);
  if (c && c.status === 'synced') return { state: 'sent', text: `✓ Report emailed to ${reportEmail.to}` };
  if (c && c.status === 'failed') return { state: 'failed', text: `Report not emailed: ${c.error || 'something went wrong'}`, changeId: c.id };
  if (!online) return { state: 'offline', text: `No signal — the report will be emailed to ${reportEmail.to} when you’re back online` };
  return { state: 'sending', text: `Emailing the report to ${reportEmail.to} (attaching the quotes)…` };
};

const NextStep = ({ inspectionId, customerName, packages = [], reportText, reportEmail, onViewReport, onDone }) => {
  const text = useReportText(reportText);
  const email = useReportEmail(inspectionId, reportEmail);
  // the customer must have the report before the agent moves on; a failure shows with Retry (and keeps
  // retrying in the background) — no signal / no number never blocks
  const textPending = !!text && text.state === 'sending';
  const [outcome, setOutcome] = useState(null);
  const [pkgIdx, setPkgIdx] = useState(packages.length === 1 ? 0 : null);
  const [preset, setPreset] = useState(null); // '3d' | '1w' | '1m' | 'date'
  const [dueDate, setDueDate] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const priced = useMemo(() => packages.map((p) => ({ name: p.name || 'Quote', total: quoteTotals(p).total })), [packages]);
  const today = localDate(new Date());

  const pick = (id) => {
    setOutcome(id);
    setError('');
    setPreset(null);
    // office tasks default to tomorrow; follow-ups wait for a pick
    setDueDate(id === 'office_task' ? inDays(1) : '');
  };

  const ready =
    (outcome === 'book' && pkgIdx != null) ||
    (outcome === 'office_task' && note.trim() && dueDate) ||
    (outcome === 'follow_up' && dueDate);

  const finish = () => {
    try {
      saveOutcome({
        inspectionId,
        customerName,
        outcome,
        note,
        dueDate,
        pkg: outcome === 'book' ? priced[pkgIdx] : null,
      });
      onDone();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="next-step">
      <div className="next-step-card">
        <div className="next-step-sent">✓ Report &amp; quotes sent</div>
        {text && (
          <div className={`next-step-text ${text.state}`}>
            <span>{text.text}</span>
            {text.state === 'failed' && (
              <button type="button" onClick={() => retryChange(text.changeId)}>
                Retry
              </button>
            )}
          </div>
        )}
        {email && (
          <div className={`next-step-text ${email.state}`}>
            <span>{email.text}</span>
            {email.state === 'failed' && (
              <button type="button" onClick={() => retryChange(email.changeId)}>
                Retry
              </button>
            )}
          </div>
        )}
        <h2>What’s next for {customerName || 'this customer'}?</h2>

        <div className="next-step-options">
          {OPTIONS.map((o) => (
            <button
              key={o.id}
              type="button"
              className={`next-step-option${outcome === o.id ? ' active' : ''}`}
              onClick={() => pick(o.id)}
            >
              <span className="next-step-icon">{o.icon}</span>
              <span>
                <span className="next-step-title">{o.title}</span>
                <span className="next-step-hint">{o.hint}</span>
              </span>
            </button>
          ))}
        </div>

        {outcome === 'book' && (
          <div className="next-step-panel">
            <div className="next-step-label">Which package did they book?</div>
            {priced.map((p, i) => (
              <button
                key={`${p.name}-${i}`}
                type="button"
                className={`next-step-pkg${pkgIdx === i ? ' active' : ''}`}
                onClick={() => setPkgIdx(i)}
              >
                <span>{p.name}</span>
                <strong>{money(p.total)}</strong>
              </button>
            ))}
          </div>
        )}

        {outcome === 'follow_up' && (
          <div className="next-step-panel">
            <div className="next-step-label">Follow up in</div>
            <div className="next-step-chips">
              {FOLLOW_UP_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`next-step-chip${preset === p.id ? ' active' : ''}`}
                  onClick={() => {
                    setPreset(p.id);
                    setDueDate(p.date());
                  }}
                >
                  {p.label}
                </button>
              ))}
              <button
                type="button"
                className={`next-step-chip${preset === 'date' ? ' active' : ''}`}
                onClick={() => {
                  setPreset('date');
                  setDueDate('');
                }}
              >
                📅 Pick a date
              </button>
            </div>
            {preset === 'date' && (
              <input type="date" className="next-step-date" min={today} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            )}
            {dueDate && <div className="next-step-when">Call on {niceDate(dueDate)}</div>}
          </div>
        )}

        {outcome === 'office_task' && (
          <div className="next-step-panel">
            <div className="next-step-label">Due date</div>
            <input type="date" className="next-step-date" min={today} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>
        )}

        {outcome && (
          <div className="next-step-panel">
            <div className="next-step-label">
              {outcome === 'office_task' ? 'What does the office need to do?' : 'Note (optional)'}
            </div>
            <textarea
              className="next-step-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={outcome === 'office_task' ? 'e.g. Send the rebate forms, confirm the price with the manager…' : 'Anything the next person should know'}
            />
          </div>
        )}

        {error && <div className="next-step-error">{error}</div>}

        <button type="button" className="next-step-finish" disabled={!ready || textPending} onClick={finish}>
          {textPending ? 'Waiting for the report text…' : 'Save & back to Route'}
        </button>
        {onViewReport && (
          <button type="button" className="next-step-link" onClick={onViewReport}>
            View the report
          </button>
        )}
      </div>
    </div>
  );
};

export default NextStep;
